import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar';
import EmptyState from '../shared/EmptyState';
import { sk } from '../shared/skeletonClass';
import type { AgentRanking } from '@types';

interface AgentLeaderboardProps {
  data: AgentRanking[];
  isLoading: boolean;
}

function getInitials (name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const RANK_COLORS = [
  'bg-amber-500',
  'bg-slate-400',
  'bg-orange-600',
  'bg-muted-foreground',
  'bg-muted-foreground',
];

/**
 * Agents ranked by transfers.
 *
 * Deliberately NOT ranked on appointments: the appointment is set by the SDR
 * after the transfer, so it is not the agent's to claim. Volume and transfers
 * are what the agent controls.
 */
export default function AgentLeaderboard ({ data, isLoading }: AgentLeaderboardProps): React.JSX.Element {
  const { t } = useTranslation();
  // Guard against 0 (nobody transferred) so bar widths don't become NaN%.
  const topTransfers = data.length > 0 ? Math.max(...data.map((a) => a.transfers)) : 0;
  const maxTransfers = topTransfers > 0 ? topTransfers : 1;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <div className={`h-5 w-36 mb-1.5 ${sk}`} />
            <div className={`h-3 w-52 ${sk}`} />
          </div>
          <div className={`h-7 w-24 ${sk}`} />
        </div>
        <div className="px-5 pb-5 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-4 h-4 ${sk}`} />
              <div className={`h-7 w-7 rounded-full ${sk}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <div className={`h-4 w-28 ${sk}`} />
                  <div className={`h-4 w-6 ${sk}`} />
                </div>
                <div className={`h-1.5 w-full rounded-full ${sk}`} />
                <div className="flex items-center justify-between mt-1.5">
                  <div className={`h-3 w-14 ${sk}`} />
                  <div className={`h-3 w-20 ${sk}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-start justify-between p-5 pb-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            {t('dashboard.overview.agentLeaderboard.title', { defaultValue: 'Agent Leaderboard' })}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.overview.agentLeaderboard.description', {
              defaultValue: 'Calls dialled and transfers handed over',
            })}
          </p>
        </div>
        <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-md border border-border">
          {t('dashboard.overview.leaderboard.transfers', { defaultValue: 'Transfers' })}
        </span>
      </div>
      <div className="px-5 pb-5 space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full">
        {data.length === 0 && (
          <EmptyState
            message={t('dashboard.overview.agentLeaderboard.empty', {
              defaultValue: 'No agent activity for this period',
            })}
          />
        )}
        {data.map((agent, index) => (
          <div key={agent.agentId} className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground w-4 text-right tabular-nums">
              {index + 1}
            </span>

            <Avatar className="h-7 w-7">
              <AvatarFallback className={`text-[10px] text-white ${RANK_COLORS[index] ?? 'bg-muted-foreground'}`}>
                {getInitials(agent.name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground truncate">{agent.name}</span>
                <span className="text-sm font-bold tabular-nums text-foreground ml-2">{agent.transfers}</span>
              </div>
              <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${(agent.transfers / maxTransfers) * 100}%` }}
                />
              </div>
              {/* gap + truncate so the two labels never collide once the card
                  is only half the row wide. */}
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-[11px] text-muted-foreground truncate">
                  {t('dashboard.overview.leaderboard.calls', { count: agent.calls })}
                </span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
                  {t('dashboard.overview.agentLeaderboard.sessions', {
                    defaultValue: '{{count}} sessions',
                    count: agent.sessions,
                  })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
